import { db } from './config';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, setDoc, updateDoc } from 'firebase/firestore';

/**
 * Returns the unread message count for a given user in a chat document.
 * Handles both the modern nested map { unreadCount: { [uid]: n } }
 * and legacy flat fields while ensuring 0 is never treated as falsy fallback.
 */
export const getChatUnreadCount = (chat, userId) => {
    if (!chat || !userId) return 0;
    if (chat.unreadCount && typeof chat.unreadCount === 'object' && chat.unreadCount[userId] !== undefined) {
        return Math.max(0, Number(chat.unreadCount[userId]) || 0);
    }
    if (chat[`unreadCount.${userId}`] !== undefined) {
        return Math.max(0, Number(chat[`unreadCount.${userId}`]) || 0);
    }
    return 0;
};

/**
 * Safely marks a chat as read for a given user in Firestore.
 * Updates both the nested map and the dot-notation path in separate operations
 * to prevent Firestore "Multiple properties refer to the same field" errors.
 */
export const markChatAsRead = async (chatId, userId) => {
    if (!chatId || !userId) return;
    try {
        const chatRef = doc(db, 'chats', chatId);
        // 1. Set nested map key to 0 with merge
        await setDoc(chatRef, {
            unreadCount: {
                [userId]: 0
            }
        }, { merge: true });

        // 2. Also zero out the dotted path if legacy updateDoc was used
        try {
            await updateDoc(chatRef, {
                [`unreadCount.${userId}`]: 0
            });
        } catch (_) {}
    } catch (err) {
        console.error("Error marking chat as read:", err);
    }
};


/**
 * Parses text for @username mentions, finds the corresponding user IDs,
 * and creates a 'mention' notification for each valid user found.
 */
export const processMentions = async (text, fromUserId, fromUserName, postId, postTextSnippet, mentionContext) => {
    const mentionRegex = /@([a-z0-9_]+)/g;
    const mentions = text.match(mentionRegex);

    if (!mentions || mentions.length === 0) {
        return;
    }

    const uniqueUsernames = [...new Set(mentions)]
        .map(u => u.substring(1).toLowerCase().trim())
        .filter(Boolean)
        .slice(0, 10);

    if (uniqueUsernames.length === 0) {
        return;
    }

    try {
        const usersQuery = query(collection(db, 'users'), where('username', 'in', uniqueUsernames));
        const usersSnapshot = await getDocs(usersQuery);

        usersSnapshot.forEach(userDoc => {
            const mentionedUserId = userDoc.id;
            if (mentionedUserId !== fromUserId) {
                const notificationRef = collection(db, 'users', mentionedUserId, 'notifications');
                addDoc(notificationRef, {
                    type: 'mention',
                    fromUserId: fromUserId,
                    fromUserName: fromUserName,
                    postId: postId,
                    postTextSnippet: postTextSnippet,
                    context: mentionContext, // 'post' or 'comment'
                    createdAt: serverTimestamp(),
                    read: false
                });
            }
        });
    } catch (error) {
        console.error("Error processing mentions:", error);
    }
};
